/**
 * Reading errors for the feedback components (§6.2, §6.4).
 *
 * The mock API rejects with `ApiError { code, message, retryable, status }`
 * (src/mocks/types.ts, §10.4). The feedback and data kits read that shape
 * structurally, so they accept the mock's errors, plain `Error`s and anything
 * else a query throws, and turn them into the "Could not …" frame: a reason
 * sentence and a code chip.
 */

/** Plain reason sentences per code. The page adds the subject ("Could not load transactions."). */
export const ERROR_REASONS: Readonly<Record<string, string>> = {
  TIMEOUT: 'The server did not answer in time.',
  INTERNAL: 'The server had a problem it could not recover from.',
  FORBIDDEN: 'Your role does not have access to this.',
  UNAUTHORIZED: 'Your session has ended.',
  UNAUTHENTICATED: 'Your session has ended.',
  PROVIDER_REFUSED: 'The provider refused the request.',
  LOCKED: 'It is locked for now.',
  RATE_LIMITED: 'Too many requests arrived in a short time.',
  NOT_FOUND: 'It does not exist, or it was removed.',
  CONFLICT: 'Someone else changed it first.',
  NETWORK: 'The connection dropped before the server answered.',
  VALIDATION: 'Some of the details were not accepted.',
};

const STATUS_CODES: Readonly<Record<number, string>> = {
  401: 'UNAUTHORIZED',
  403: 'FORBIDDEN',
  404: 'NOT_FOUND',
  409: 'CONFLICT',
  429: 'RATE_LIMITED',
  500: 'INTERNAL',
  504: 'TIMEOUT',
};

/** The error's code (TIMEOUT, FORBIDDEN, …), or undefined when it has none. */
export function errorCode(error: unknown): string | undefined {
  if (typeof error !== 'object' || error === null) return undefined;
  const { code, status } = error as { code?: unknown; status?: unknown };
  if (typeof code === 'string' && code) return code;
  if (typeof status === 'number') return STATUS_CODES[status];
  return undefined;
}

/**
 * The reason sentence for an error. The mock API's message is already a
 * staff-facing sentence, so an error that carries a code uses its message;
 * anything else (a thrown TypeError) never shows its raw text.
 */
export function errorReason(error: unknown): string {
  const code = errorCode(error);
  if (code && typeof error === 'object' && error !== null) {
    const { message } = error as { message?: unknown };
    if (typeof message === 'string' && message.trim()) return message.trim();
  }
  if (code && ERROR_REASONS[code]) return ERROR_REASONS[code];
  return 'The reason is not known.';
}

/** True for errors that need the person to sign in again (401). */
export function isSignedOutCode(code: string | undefined): boolean {
  return code === 'UNAUTHORIZED' || code === 'UNAUTHENTICATED';
}

/** Whether a retry can help. ApiError says so itself; unknown errors are assumed retryable. */
export function isRetryable(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return true;
  const { retryable } = error as { retryable?: unknown };
  if (typeof retryable === 'boolean') return retryable;
  const code = errorCode(error);
  return code !== 'FORBIDDEN' && !isSignedOutCode(code) && code !== 'NOT_FOUND';
}
